import { createRouter, createWebHistory } from 'vue-router';

import Layout from '../components/Layout.vue';
import MyPatients from '../views/my-patients/index.vue';
import MyCalendar from '../views/my-calendar/index.vue';
import MyWorkbench from '../views/my-workbench/index.vue';


import Availability from '../views/my-calendar/sections/availability/index.vue';
import EncounterNoteList from '../views/encounter-notes/EncounterNoteList.vue';
import EncounterNote from '../views/encounter-notes/EncounterNote.vue';

// New Workbench Components
import WorkbenchLayout from '../views/my-workbench-new/index.vue';
import WorkflowList from '../views/my-workbench-new/workflow/index.vue';
import WorkflowCanvas from '../views/my-workbench-new/workflow/WorkflowCanvas.vue';
import EZBill from '../views/my-workbench-new/ez-bill/index.vue';
import DocumentTemplate from '../views/my-workbench-new/document-template/home/index.vue';
import CreateDocument from '../views/my-workbench-new/document-template/create-document/index.vue';
import ReportFormat from '../views/my-workbench-new/report-format/index.vue';
import TemplateMarketplace from '../views/my-workbench-new/template-marketplace/index.vue';
import Archive from '../views/my-workbench-new/archive/index.vue';
import { runUnsavedChangesHandler } from '../utils/unsavedChanges';
import {
  EMBED_QUERY_KEY,
  isEmbedMode,
  isEmbedScope,
  vaTabForPath,
} from '../utils/embedMode';
import { CLINICAL_CALENDAR_HOME, isHiddenViRoute } from '../utils/viScope';

// My Practice
import ClinicDashboard from '@/views/my-practice/clinicDashboard/index.vue';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/', redirect: '/my-calendar' },
    { path: '/my-patients', component: Layout, children: [{ path: '', component: MyPatients }] },
    {
      path: '/my-calendar',
      component: Layout,
      children: [
        { path: '', component: MyCalendar },
        { path: 'availability', component: Availability }
      ]
    },
    { 
      path: '/my-workbench', 
      component: Layout, 
      children: [
        {
          path: '',
          component: WorkbenchLayout,
          children: [
            { path: '', redirect: '/my-workbench/ez-bill' },
            { path: 'ez-bill', component: EZBill },
            { path: 'document-template', component: DocumentTemplate },
            { path: 'automation', component: WorkflowList },
            { path: 'template', component: WorkflowList }, // Placeholder, can be replaced later
            { path: 'analytics', component: WorkflowList }, // Placeholder, can be replaced later
            { path: 'report-format', component: ReportFormat },
            { path: 'template-marketplace', component: TemplateMarketplace },
            { path: 'archive', component: Archive },
          ]
        },
        { path: 'document-template/create-document/:id?', component: CreateDocument },
      ]
    },
    // Workflow Canvas - Full Screen (No Layout)
    {
      path: '/my-workbench/automation/edit/:workflowId',
      component: WorkflowCanvas
    },
    {
      path: '/my-practice',
      component: Layout,
      children: [
        { path: 'clinic-dashboard', component: ClinicDashboard },
        // Resolve the tabless entry to the default tab during navigation, BEFORE
        // anything mounts. The settings page used to handle this itself with a
        // router.replace() from its route watcher, which meant mounting on the
        // tabless record and then immediately unmounting for the :tab record —
        // two different route records, so a full teardown that ran clearData()
        // and wiped every settings store module mid-flight. va-portal's iframe
        // always enters here (its Settings link is /setting with no tab).
        {
          path: 'practice-setting',
          redirect: (to) => ({
            path: '/my-practice/practice-setting/account-info',
            query: to.query,
          }),
        },
        { path: 'practice-setting/:tab', component: () => import('@/views/setting/my-practice/index.vue') },
      ]
    },
    // /my-account has been merged into /my-practice/practice-setting. Keep
    // these as redirects so any external links / bookmarks still resolve.
    // The query string (including embed=true and jwtToken=...) is preserved
    // by Vue Router's redirect logic, so an embedded session survives the
    // hop intact.
    {
      path: '/my-account',
      redirect: (to) => ({
        path: '/my-practice/practice-setting/account-info',
        query: to.query,
      }),
    },
    {
      path: '/my-account/:tab',
      redirect: (to) => ({
        path: `/my-practice/practice-setting/${to.params.tab}`,
        query: to.query,
      }),
    },
    {
      path: '/encounter-notes',
      component: Layout,
      children: [
        { path: '', component: EncounterNoteList },
        { path: ':patientId/:noteId', component: EncounterNote }
      ]
    },
  ]
})

// Preserve the embed=true flag across internal navigation between any
// embed-scope routes (practice-setting tabs, /my-account/account-info, etc.).
// Sidebar tab clicks call router.push without query params, which would
// otherwise drop the flag and bring the navbar back.
router.beforeEach((to, from, next) => {
  const toIsEmbed = isEmbedScope(to.path);
  const fromIsEmbed = isEmbedScope(from.path);
  const fromWasEmbed = isEmbedMode(from);
  const toLacksEmbed =
    to.query[EMBED_QUERY_KEY] !== 'true' && to.query[EMBED_QUERY_KEY] !== true;

  if (toIsEmbed && fromIsEmbed && fromWasEmbed && toLacksEmbed) {
    next({
      path: to.path,
      query: { ...to.query, [EMBED_QUERY_KEY]: 'true' },
      hash: to.hash,
      replace: true,
    });
    return;
  }
  next();
});

// VP-18687: only the Clinical Calendar is in use. Send any URL for a retired
// VI page back to the calendar before it mounts, so it fires no requests.
router.beforeEach((to) => {
  if (isHiddenViRoute(to)) {
    return { path: CLINICAL_CALENDAR_HOME, replace: true };
  }
});

router.beforeEach(async (to, from) => {
  return await runUnsavedChangesHandler(to, from);
});

// When embedded inside va-portal, notify the parent window after each route
// change so it can sync its own URL bar (e.g. /setting/billing,
// /setting/account) without reloading the iframe.
router.afterEach((to) => {
  if (!isEmbedMode(to)) return;
  if (typeof window === 'undefined') return;
  if (!window.parent || window.parent === window) return;

  // Resolve to the va-portal tab via the shared mapping. Undefined means
  // the route isn't mapped, so we don't sync the parent URL.
  const tab = vaTabForPath(to.path);
  if (tab === undefined) return;

  try {
    window.parent.postMessage(
      {
        type: 'va-portal:embed-route-change',
        source: 'ehr-frontend',
        path: to.path,
        tab,
      },
      '*'
    );
  } catch (err) {
    // Cross-origin postMessage failures are non-fatal; log and move on.
    console.warn('embed-route-change postMessage failed', err);
  }
});

export default router;